import type { FC } from 'react'
import { useState } from 'react'

import { Checkbox, Card, Popup } from '@skytech/ui'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field } from 'formik'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { leasingResources } from '@/resources/leasing'
import { commonQueries } from '@/resources/queries'

const Flags: FC<{ isActive: boolean; customerId?: number | null; hideBuyout?: boolean }> = ({
  isActive,
  customerId,
  hideBuyout,
}) => {
  const translate = useTranslation()
  const [isInactivationWarningModalOpen, setIsInactivationWarningModalOpen] = useState(false)
  const { data: customerVendors } = useQuery(
    commonQueries.customerVendorsList(),
    leasingResources.getCustomerVendors
  )

  // Check if the vendor for the given customer has a policy linked to them
  const isLeasingEnabled =
    Array.isArray(customerVendors) &&
    (customerId
      ? customerVendors?.filter(v => v.customerId === customerId).some(v => v.usePolicyLevelLeasing)
      : customerVendors?.some(v => v.usePolicyLevelLeasing))

  return (
    <Card className="flags">
      <h3>
        <Translation id="Misc" />
      </h3>

      <Field name="isManagerApprovalRequired">
        {({ field }: FieldProps) => (
          <Checkbox
            id="isManagerApprovalRequired"
            label={<Translation id="Manager approval required" />}
            marginBottom={16}
            checked={field.value}
            {...field}
          />
        )}
      </Field>

      <Field name="isCustomAddressActivated">
        {({ field }: FieldProps) => (
          <Checkbox
            id="isCustomAddressActivated"
            label={<Translation id="Custom delivery address" />}
            marginBottom={16}
            checked={field.value}
            {...field}
          />
        )}
      </Field>

      <Field name="isAssetReturnRequired">
        {({ field, form }: FieldProps) => (
          <Checkbox
            id="isAssetReturnRequired"
            label={<Translation id="Is asset return required" />}
            marginBottom={16}
            checked={field.value}
            onFieldChange={() => {
              if (!form.values.isBuyoutAllowed || !field.value) return
              form.setFieldValue('isBuyoutAllowed', false)
            }}
            {...field}
          />
        )}
      </Field>

      {!hideBuyout && (
        <Field name="isBuyoutAllowed">
          {({ field, form }: FieldProps) => (
            <Checkbox
              id="isBuyoutAllowed"
              label={<Translation id="Is buyout allowed" />}
              marginBottom={16}
              checked={field.value}
              isDisabled={!form.values.isAssetReturnRequired}
              {...field}
            />
          )}
        </Field>
      )}

      <Field name="isActive">
        {({ field }: FieldProps) => (
          <Checkbox
            id="isActive"
            label={<Translation id="Is active" />}
            marginBottom={16}
            checked={field.value}
            {...field}
            onChange={e => {
              if (isActive && field.value) {
                setIsInactivationWarningModalOpen(true)
              }
              field.onChange(e)
            }}
          />
        )}
      </Field>

      {isLeasingEnabled && (
        <Field name="isLeasing">
          {({ field }: FieldProps) => (
            <Checkbox
              id="isLeasing"
              label={<Translation id="Lease" />}
              marginBottom={16}
              checked={field.value}
              isDisabled={!isLeasingEnabled}
              {...field}
            />
          )}
        </Field>
      )}

      <Popup
        isOpen={isInactivationWarningModalOpen}
        ariaLabel={translate('Inactive policy warning')}
        onClose={() => setIsInactivationWarningModalOpen(false)}
      >
        <>
          <h3>
            <Translation id="Warning" />
          </h3>

          <p>
            <Translation
              id="If you update and save this policy as inactive, all policy subscribers will be unsubscribed
              and unable to order more products from this policy's selection."
            />
          </p>
        </>
      </Popup>
    </Card>
  )
}

export default Flags
